import { type ReactNode } from "react";
import { Navigate, useLocation } from "@tanstack/react-router";
import { useAuth, type AppRole } from "@/features/auth/AuthContext";
import { Spinner } from "@/components/ui/Spinner";

interface GuardProps {
  children: ReactNode;
}

/** Loading screen shown while session is being resolved. */
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Spinner size="lg" label="A verificar sessão..." />
    </div>
  );
}

/**
 * Rota apenas para utilizadores não autenticados (login, recuperar-password, etc.).
 *
 * Se o utilizador já tiver sessão completa (aal2) redireciona para /dashboard.
 * Se a sessão estiver incompleta (aal1, falta o passo TOTP) deixa ficar no
 * /login para o utilizador inserir o codigo. Isto evita um loop infinito entre
 * /dashboard (que exige aal2) e /login.
 */
export function PublicOnlyRoute({ children }: GuardProps) {
  const { session, aal, loading } = useAuth();

  if (loading) return <AuthLoadingScreen />;
  if (session && aal === "aal2") return <Navigate to="/dashboard" />;

  return <>{children}</>;
}

/**
 * Rota que exige autenticação básica (aal1 no mínimo).
 * Redireciona para /login se não houver sessão.
 * Redireciona para /onboarding se must_change_password=true.
 */
export function RequireAuth({ children }: GuardProps) {
  const { session, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) return <AuthLoadingScreen />;

  if (!session) {
    return <Navigate to="/login" search={{ redirect: location.pathname }} />;
  }

  // Primeiro login — força onboarding
  if (profile?.must_change_password && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" />;
  }

  return <>{children}</>;
}

/**
 * Rota que exige AAL2 (TOTP verificado na sessão actual).
 *
 * - Sessão aal2 → passa.
 * - Sessão aal1 sem factor MFA enrolado (needsMfaSetup) → onboarding para
 *   configurar 2FA (não redireccionar para /login, pois isso causaria loop
 *   infinito porque o login nunca conseguiria subir para aal2).
 * - Sessão aal1 com factor enrolado → /login para introduzir codigo TOTP.
 */
export function RequireAal2({ children }: GuardProps) {
  const { session, aal, needsMfaSetup, loading } = useAuth();

  if (loading) return <AuthLoadingScreen />;

  if (!session) {
    return <Navigate to="/login" />;
  }

  if (aal !== "aal2") {
    if (needsMfaSetup) {
      // Não tem factor MFA — mandar configurar primeiro
      return <Navigate to="/onboarding" />;
    }
    // Tem factor enrolado mas não verificou nesta sessão
    return <Navigate to="/login" search={{ mfa_required: "true" }} />;
  }

  return <>{children}</>;
}

/**
 * Rota que exige role admin.
 * Lê os roles activos do utilizador via AuthContext (tabela practitioner_roles).
 */
export function RequireAdmin({ children }: GuardProps) {
  return <RequireRole roles={["admin"]}>{children}</RequireRole>;
}

/**
 * Rota que exige um ou mais roles activos. Admin tem sempre acesso a tudo.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: AppRole[];
  children: ReactNode;
}) {
  const {
    session,
    aal,
    needsMfaSetup,
    loading,
    roles: userRoles,
  } = useAuth();

  if (loading) return <AuthLoadingScreen />;

  if (!session) {
    return <Navigate to="/login" />;
  }

  if (aal !== "aal2") {
    if (needsMfaSetup) {
      return <Navigate to="/onboarding" />;
    }
    return <Navigate to="/login" search={{ mfa_required: "true" }} />;
  }

  const allowed =
    userRoles.includes("admin") || roles.some((r) => userRoles.includes(r));

  if (!allowed) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
}
